(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./ai-chat-core.js'), null);
    return;
  }

  if (root) {
    root.MarineMaxAIChatApi = factory(root.MarineMaxAIChatCore, root);
  }
})(typeof window !== 'undefined' ? window : null, function (core, root) {
  'use strict';

  if (!core) {
    throw new Error('MarineMax AI Chat core is required by the API module');
  }

  const defaultChatEndpoint =
    'https://shopify-ai-sales-agent-proxy.vercel.app/api/inventoryiq-proxy';
  const defaultHubSpotSubmitBaseUrl =
    'https://api.hsforms.com/submissions/v3/integration/submit';
  const defaultInventoryIqBaseUrl = 'https://api.inventoryiq.co';

  function createAbortError(message) {
    const error = new Error(message);
    error.name = 'AbortError';
    return error;
  }

  function createChatClient(options = {}) {
    const fetchImpl = options.fetch || (root && root.fetch ? root.fetch.bind(root) : null);
    const AbortControllerClass =
      options.AbortController ||
      (root && root.AbortController) ||
      (typeof AbortController !== 'undefined' ? AbortController : null);
    const setTimer =
      options.setTimeout ||
      (root && root.setTimeout
        ? root.setTimeout.bind(root)
        : globalThis.setTimeout.bind(globalThis));
    const clearTimer =
      options.clearTimeout ||
      (root && root.clearTimeout
        ? root.clearTimeout.bind(root)
        : globalThis.clearTimeout.bind(globalThis));
    const endpoint = options.endpoint || defaultChatEndpoint;
    const maxNetworkRetries =
      options.maxNetworkRetries ?? core.constants.AI_CHAT_MAX_NETWORK_RETRIES;
    const requestTimeoutMs =
      options.requestTimeoutMs ?? core.constants.AI_CHAT_REQUEST_TIMEOUT_MS;
    const retryDelayMs = options.retryDelayMs ?? core.constants.RETRY_DELAY;
    const delay = options.delay || ((milliseconds) => new Promise((resolve) => setTimer(resolve, milliseconds)));

    if (typeof fetchImpl !== 'function') {
      throw new Error('A fetch implementation is required by the AI Chat client');
    }
    if (!AbortControllerClass) {
      throw new Error('AbortController is required by the AI Chat client');
    }

    let activeController = null;

    function cancel() {
      if (!activeController) return;
      activeController.abort();
      activeController = null;
    }

    function release() {
      activeController = null;
    }

    async function request(payload, requestOptions = {}) {
      const isCurrent =
        typeof requestOptions.isCurrent === 'function'
          ? requestOptions.isCurrent
          : () => true;
      let lastError = null;

      for (let attempt = 0; attempt <= maxNetworkRetries; attempt += 1) {
        if (!isCurrent()) {
          throw createAbortError('AI chat request was superseded');
        }

        const requestController = new AbortControllerClass();
        activeController = requestController;
        const timeoutId = setTimer(() => requestController.abort(), requestTimeoutMs);

        try {
          const response = await fetchImpl(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/markdown',
            },
            body: JSON.stringify(payload),
            signal: requestController.signal,
          });
          clearTimer(timeoutId);
          return response;
        } catch (networkError) {
          clearTimer(timeoutId);
          lastError = networkError;

          if (!isCurrent() || attempt >= maxNetworkRetries) {
            throw networkError;
          }

          await delay(retryDelayMs * (attempt + 1));
        }
      }

      throw lastError || new Error('AI chat request failed');
    }

    return Object.freeze({
      cancel,
      get hasActiveRequest() {
        return activeController !== null;
      },
      release,
      request,
    });
  }

  function createInventoryIqClient(options = {}) {
    const fetchImpl = options.fetch || (root && root.fetch ? root.fetch.bind(root) : null);
    const baseUrl = (options.baseUrl || defaultInventoryIqBaseUrl).replace(/\/+$/, '');

    if (typeof fetchImpl !== 'function') {
      throw new Error('A fetch implementation is required by the InventoryIQ client');
    }

    function createHeaders(authToken) {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      return headers;
    }

    async function postJson(path, payload, requestOptions = {}) {
      const response = await fetchImpl(`${baseUrl}${path}`, {
        method: 'POST',
        headers: createHeaders(requestOptions.authToken),
        body: JSON.stringify(payload),
      });
      let bodyText = '';

      try {
        bodyText = await response.text();
      } catch (readError) {
        if (!response.ok && requestOptions.throwOnHttpError !== false) {
          throw new Error(`HTTP ${response.status}`);
        }
        throw readError;
      }

      if (!response.ok && requestOptions.throwOnHttpError !== false) {
        const errorSuffix = bodyText ? `: ${bodyText}` : '';
        throw new Error(`HTTP ${response.status}${errorSuffix}`);
      }

      let data = {};
      let parseError = null;

      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch (error) {
        parseError = error;
      }

      if (requestOptions.requireJson && (!bodyText || parseError)) {
        throw parseError || new SyntaxError('InventoryIQ returned an empty JSON response');
      }

      return {
        data,
        ok: response.ok,
        parseError,
        status: response.status,
      };
    }

    async function startPasswordless(email) {
      return postJson('/auth/passwordless/start', { email });
    }

    async function verifyPasswordless(code, email) {
      return postJson(
        '/auth/passwordless/token',
        { email, code },
        { throwOnHttpError: false },
      );
    }

    async function createQuote(payload, authToken) {
      const response = await postJson('/quotes/shopify', payload, {
        authToken,
        requireJson: true,
      });
      return response.data;
    }

    async function sendQuoteEmail(quoteId, payload, authToken) {
      const response = await postJson(`/quotes/${quoteId}/send-email`, payload, {
        authToken,
        requireJson: true,
      });
      return response.data;
    }

    return Object.freeze({
      createQuote,
      sendQuoteEmail,
      startPasswordless,
      verifyPasswordless,
    });
  }

  function createHubSpotClient(options = {}) {
    const fetchImpl = options.fetch || (root && root.fetch ? root.fetch.bind(root) : null);
    const baseUrl = (options.baseUrl || defaultHubSpotSubmitBaseUrl).replace(/\/+$/, '');

    if (typeof fetchImpl !== 'function') {
      throw new Error('A fetch implementation is required by the HubSpot client');
    }

    async function submit(portalId, formId, payload) {
      const response = await fetchImpl(
        `${baseUrl}/${encodeURIComponent(portalId)}/${encodeURIComponent(formId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`HubSpot request failed with status ${response.status}`);
      }

      return response;
    }

    return Object.freeze({
      submit,
    });
  }

  return Object.freeze({
    createChatClient,
    createHubSpotClient,
    createInventoryIqClient,
    defaultChatEndpoint,
    defaultHubSpotSubmitBaseUrl,
    defaultInventoryIqBaseUrl,
  });
});
